import React, { useState, useMemo } from "react";

const MetricTable = ({ columns, data, sortable, striped, className }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (!sortable) return;

    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data) return [];
    if (!sortKey) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      // Handle nulls
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Numeric comparison
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (sortDir === "asc") {
        return aStr.localeCompare(bStr);
      }

      return bStr.localeCompare(aStr);
    });
  }, [data, sortKey, sortDir]);

  const getAlignClass = (align) => {
    switch (align) {
      case "center":
        return "text-center";
      case "right":
        return "text-right";
      default:
        return "text-left";
    }
  };

  if (!columns || !data) return null;

  return (
    <div className={`overflow-x-auto -mx-4 sm:mx-0 ${className || ""}`}>
      <table className="w-full" style={{ minWidth: 500 }}>
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            {columns.map((col) => (
              <th
                className={`text-xs font-medium text-gray-500 dark:text-gray-400 uppercase py-3 px-4 ${getAlignClass(col.align)} ${sortable ? "cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors" : ""}`}
                key={col.key}
                onClick={() => handleSort(col.key)}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {sortable && sortKey === col.key && (
                    <i
                      className={`fas fa-arrow-${sortDir === "asc" ? "up" : "down"} text-[10px] text-primary-500`}
                    />
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, rowIndex) => (
            <tr
              className={`border-b border-gray-100 dark:border-gray-700/50 ${
                striped !== false
                  ? rowIndex % 2 === 0
                    ? "table-row-striped-even"
                    : "table-row-striped-odd"
                  : ""
              } table-row-hover`}
              key={row.id ?? rowIndex}
            >
              {columns.map((col) => {
                const rawVal = row[col.key];
                const displayVal = col.format
                  ? col.format(rawVal, row)
                  : rawVal;

                return (
                  <td
                    className={`py-3 px-4 text-sm ${getAlignClass(col.align)}`}
                    key={col.key}
                  >
                    {displayVal}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MetricTable;
